import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../../../../../features/userSlice";

import { STOperationEndPointLogic } from "../../../../../logic/PaidTrafficManagement/SearchTermOperation/searchTermOperationEndPointLogic";
import {
  setIsAiRecDataFetched,
  setKeywordRecommendations,
  // setLoadingAiRecomendation,
  setLoadingAiRecomendationInner,
  setPositiveSTLoader,
} from "../../../../../features/aiRecomendationTabSlice";

const QUERY_IDS = {
  AI_REC_QUERY: "5642933638266880",
  Positive_ST_QUERY: "5653784671813632",
  Negetive_ST_QUERY: "5695228287647744",
  Exact_ST_QUERY: "5644784165191680",
  ST_LEVEL_QUERY: "5756247156457472",
  ST_KEYWORD_QUERY: "5747763555860480",
  NEGATIVE_ST_KEYWORD_QUERY: "5633064340291584",
};

export const useSTOAIRecommendations = () => {
  const dispatch = useDispatch();
  const oauthToken = useSelector(selectUserToken) as any;
  const [searchParams] = useSearchParams();

  const selectedSiteUid = searchParams.get("selectedSite") || "";
  const selectedAccountUid = searchParams.get("account") || "";
  const scopeUidFromURL = searchParams.get("scopeUid") || "";

  const fetchAIRecommendations = async (overrideScopeUid?: string) => {
    try {
      // dispatch(setLoadingAiRecomendation(true));
      dispatch(setIsAiRecDataFetched(false));
      dispatch(setLoadingAiRecomendationInner(true));
      const scopeUidToUse = overrideScopeUid || scopeUidFromURL;

      if (!scopeUidToUse) {
        dispatch(
          setDisplayErrorSnackBar({
            message: "Missing scope UID for recommendation fetch.",
            type: "error",
          })
        );
        return;
      }

      const response =
        await STOperationEndPointLogic.getSTOperationAIRecommendation(
          oauthToken,
          QUERY_IDS.AI_REC_QUERY,
          selectedSiteUid,
          selectedAccountUid,
          scopeUidToUse
        );

      return response;
    } catch (error) {
      dispatch(
        setDisplayErrorSnackBar({
          message:
            "Failed to fetch AI recommendations. Please try again later.",
          type: "error",
        })
      );

      console.error("AI Recommendation Error:", error);
      return null;
    } finally {
      // dispatch(setLoadingAiRecomendation(false));
      dispatch(setIsAiRecDataFetched(true));
      dispatch(setLoadingAiRecomendationInner(false));
    }
  };

  const fetchAIRecommendationsWithoutLoader = async (
    overrideScopeUid?: string
  ) => {
    try {
      dispatch(setIsAiRecDataFetched(false));
      dispatch(setLoadingAiRecomendationInner(true));
      const scopeUidToUse = overrideScopeUid || scopeUidFromURL;

      if (!scopeUidToUse) {
        dispatch(
          setDisplayErrorSnackBar({
            message: "Missing scope UID for recommendation fetch.",
            type: "error",
          })
        );
        return;
      }

      const response =
        await STOperationEndPointLogic.getSTOperationAIRecommendation(
          oauthToken,
          QUERY_IDS.AI_REC_QUERY,
          selectedSiteUid,
          selectedAccountUid,
          scopeUidToUse
        );

      return response;
    } catch (error) {
      dispatch(
        setDisplayErrorSnackBar({
          message:
            "Failed to fetch AI recommendations. Please try again later.",
          type: "error",
        })
      );

      console.error("AI Recommendation Error:", error);
      return null;
    } finally {
      dispatch(setIsAiRecDataFetched(true));
      dispatch(setLoadingAiRecomendationInner(false));
    }
  };

  const fetchPositiveST = async (keyword_shared_uid: string) => {
    try {
      dispatch(setPositiveSTLoader(true));
      const keywordUidToUse = keyword_shared_uid;

      if (!keywordUidToUse) {
        dispatch(
          setDisplayErrorSnackBar({
            message: "Missing keyword_shared_uid for recommendation fetch.",
            type: "error",
          })
        );
        return;
      }

      const response = await STOperationEndPointLogic.getPositiveST(
        oauthToken,
        QUERY_IDS.Positive_ST_QUERY,
        keywordUidToUse,
        selectedSiteUid
      );

      return response;
    } catch (error) {
      dispatch(
        setDisplayErrorSnackBar({
          message: "Failed to fetch Search Term. Please try again later.",
          type: "error",
        })
      );

      return null;
    } finally {
      dispatch(setPositiveSTLoader(false));
    }
  };

  const fetchNegetiveST = async (keyword_shared_uid?: string) => {
    try {
      dispatch(setPositiveSTLoader(true));
      const keywordUidToUse = keyword_shared_uid;

      if (!keywordUidToUse) {
        dispatch(
          setDisplayErrorSnackBar({
            message: "Missing keyword_shared_uid for recommendation fetch.",
            type: "error",
          })
        );
        return;
      }

      const response = await STOperationEndPointLogic.getNegetiveST(
        oauthToken,
        QUERY_IDS.Negetive_ST_QUERY,
        keywordUidToUse,
        selectedSiteUid
      );

      return response;
    } catch (error) {
      dispatch(
        setDisplayErrorSnackBar({
          message: "Failed to fetch Search Term. Please try again later.",
          type: "error",
        })
      );

      return null;
    } finally {
      dispatch(setPositiveSTLoader(false));
    }
  };

  const getAllExactST = async (ad_shared_uid?: string) => {
    try {
      const adSharedUidToUse = ad_shared_uid;

      if (!adSharedUidToUse) {
        dispatch(
          setDisplayErrorSnackBar({
            message: "Missing ad_shared_uid .",
            type: "error",
          })
        );
        return;
      }

      const response = await STOperationEndPointLogic.getAllExactST(
        oauthToken,
        QUERY_IDS.Exact_ST_QUERY,
        adSharedUidToUse,
        selectedSiteUid
      );

      return response;
    } catch (error) {
      dispatch(
        setDisplayErrorSnackBar({
          message: "Failed to fetch Search Term. Please try again later.",
          type: "error",
        })
      );

      return null;
    } finally {
      dispatch(setPositiveSTLoader(false));
    }
  };

  const removeNegativeTermsAi = async (keyword_shared_uid?: string) => {
    try {
      dispatch(setPositiveSTLoader(true));
      const data = [] as any;

      const keywordUidToUse = keyword_shared_uid;

      if (!keywordUidToUse) {
        dispatch(
          setDisplayErrorSnackBar({
            message: "Missing keyword_shared_uid for recommendation fetch.",
            type: "error",
          })
        );
        return;
      }

      const response = await STOperationEndPointLogic.removeNegativeTermsAi(
        oauthToken,
        data,
        keyword_shared_uid
      );
      dispatch(
        setKeywordRecommendations({
          keyword_shared_uid: keyword_shared_uid,
          data: response?.data?.data, // or however your API response is structured
        })
      );

      return response;
    } catch (error) {
      dispatch(
        setDisplayErrorSnackBar({
          message: "Failed to fetch Search Term. Please try again later.",
          type: "error",
        })
      );

      return null;
    }
  };

  const handleRemoveNegativeAPI = async (sharedUid: string) => {
    try {
      // Prepare the payload according to your API requirements
      const payload = {
        status: "mark_as_removed", // status: mark_as_ignored / mark_as_removed
      };

      const response = await STOperationEndPointLogic.updateRecommendation(
        oauthToken,
        payload,
        sharedUid
      );
      if (
        response &&
        (response.status === 200 || response.status === 201 || response.success)
      ) {
        return response;
      } else {
        throw new Error(`API returned unsuccessful status for ${sharedUid}`);
      }
    } catch (error) {
      console.error(`Error removing negative terms for ${sharedUid}:`, error);
      // Re-throw the error so it can be caught in the parent handler
      throw error;
    }
  };

  const handleIgnoreAPI = async (sharedUid: string) => {
    try {
      const payload = {
        status: "mark_as_ignored", // status: mark_as_ignored / mark_as_removed
      };

      const response = await STOperationEndPointLogic.updateRecommendation(
        oauthToken,
        payload,
        sharedUid
      );
      if (
        response &&
        (response.status === 200 || response.status === 201 || response.success)
      ) {
        return response;
      } else {
        throw new Error(`API returned unsuccessful status for ${sharedUid}`);
      }
    } catch (error) {
      console.error(`Error ignoring recommendation for ${sharedUid}:`, error);
      // Re-throw the error so it can be caught in the parent handler
      throw error;
    }
  };

  const getAllSTLevel = async (overrideScopeUid?: string) => {
    try {
      // dispatch(setLoadingAiRecomendation(true));
      dispatch(setIsAiRecDataFetched(false));
      dispatch(setLoadingAiRecomendationInner(true));
      const scopeUidToUse = overrideScopeUid || scopeUidFromURL;

      if (!scopeUidToUse) {
        dispatch(
          setDisplayErrorSnackBar({
            message: "Missing scope UID for recommendation fetch.",
            type: "error",
          })
        );
        return;
      }

      const response = await STOperationEndPointLogic.getAllSTLevel(
        oauthToken,
        QUERY_IDS.ST_LEVEL_QUERY,
        selectedSiteUid,
        selectedAccountUid,
        scopeUidToUse
      );

      return response;
    } catch (error) {
      dispatch(
        setDisplayErrorSnackBar({
          message:
            "Failed to fetch AI recommendations. Please try again later.",
          type: "error",
        })
      );

      console.error("AI Recommendation Error:", error);
      return null;
    } finally {
      // dispatch(setLoadingAiRecomendation(false));
      dispatch(setIsAiRecDataFetched(true));
      dispatch(setLoadingAiRecomendationInner(false));
    }
  };

  const getPositiveKeywordDetails = async (
    search_term: string,
    search_term_match_type: string
  ) => {
    try {
      dispatch(setPositiveSTLoader(true));
      const scopeUidToUse = scopeUidFromURL;

      if (!scopeUidToUse) {
        dispatch(
          setDisplayErrorSnackBar({
            message: "Missing scope UID",
            type: "error",
          })
        );
        return;
      }

      const response = await STOperationEndPointLogic.getKeywordDetails(
        QUERY_IDS.ST_KEYWORD_QUERY,
        oauthToken,
        selectedSiteUid,
        scopeUidToUse,
        search_term,
        search_term_match_type
      );

      return response;
    } catch (error) {
      dispatch(
        setDisplayErrorSnackBar({
          message: "Failed to fetch Keyword Details. Please try again later.",
          type: "error",
        })
      );

      console.error("Keyword Details Error:", error);
      return null;
    } finally {
      dispatch(setPositiveSTLoader(false));
    }
  };

  const getNegetiveKeywordDetails = async (
    search_term: string,
    search_term_match_type: string
  ) => {
    try {
      dispatch(setPositiveSTLoader(true));
      const scopeUidToUse = scopeUidFromURL;

      if (!scopeUidToUse) {
        dispatch(
          setDisplayErrorSnackBar({
            message: "Missing scope UID",
            type: "error",
          })
        );
        return;
      }

      const response = await STOperationEndPointLogic.getKeywordDetailsNegetive(
        QUERY_IDS.NEGATIVE_ST_KEYWORD_QUERY,
        oauthToken,
        selectedSiteUid,
        scopeUidToUse,
        search_term,
        search_term_match_type
      );

      return response;
    } catch (error) {
      dispatch(
        setDisplayErrorSnackBar({
          message: "Failed to fetch Keyword Details. Please try again later.",
          type: "error",
        })
      );

      console.error("Keyword Details Error:", error);
      return null;
    } finally {
      dispatch(setPositiveSTLoader(false));
    }
  };

  const removeNegativeSearchTerm = async (reqBody: any) => {
    let successCount = 0;
    let failCount = 0;
    let errorDetails: string[] = [];

    try {
      const response = await STOperationEndPointLogic.removeNegativeTerms(
        oauthToken,
        reqBody
      );

      if (!response) {
        throw new Error("No response from server");
      }

      const { reason, data } = response;
      const results = data?.results ?? [];

      // HARD FAIL
      if (reason !== "Success" && reason !== "Partial Success") {
        throw new Error(reason || "Failed to remove negative terms");
      }

      // PROCESS RESULTS LIKE A NORMAL HUMAN
      for (const item of results) {
        if (item.status === "success") {
          successCount++;
        } else {
          failCount++;
          errorDetails.push(
            `${item.term ?? "Unknown term"}: ${item.error ?? "Unknown error"}`
          );
        }
      }

      // SINGLE SNACKBAR — NOT SPAM
      if (failCount === 0) {
        dispatch(
          setDisplayErrorSnackBar({
            message: `Successfully removed ${successCount} negative term(s)`,
            type: "success",
          })
        );
      } else {
        dispatch(
          setDisplayErrorSnackBar({
            message: `Removed ${successCount} term(s). ${failCount} failed. ${errorDetails.join(
              "; "
            )}`,
            type: "warning",
          })
        );
      }

      return { successCount, failCount };
    } catch (error) {
      const err = error as any;
      const failedCount = Array.isArray(reqBody?.terms)
        ? reqBody.terms.length
        : 0;

      const errorMessage =
        err?.response?.data?.reason ||
        err?.parsedText?.reason ||
        err?.message ||
        "Failed to remove negative terms";

      dispatch(
        setDisplayErrorSnackBar({
          message: errorMessage,
          type: "error",
        })
      );

      return { successCount: 0, failCount: failedCount };
    }
  };

  return {
    fetchAIRecommendations,
    fetchPositiveST,
    fetchNegetiveST,
    removeNegativeTermsAi,
    handleRemoveNegativeAPI,
    handleIgnoreAPI,
    fetchAIRecommendationsWithoutLoader,
    getAllExactST,
    getAllSTLevel,
    getPositiveKeywordDetails,
    getNegetiveKeywordDetails,
    removeNegativeSearchTerm,
  };
};
